/** The side of the square the photo is cropped and resized to, in pixels. */
export const PHOTO_SIZE = 256;

/** JPEG at this quality lands a 256px photo around 15 to 40 KB, whatever the original. */
const PHOTO_QUALITY = 0.85;

/**
 * Decoded the right way up. `imageOrientation: 'from-image'` is what makes a portrait
 * phone photo come out upright, but the value is recent (Chrome 115, Safari 17), and a
 * browser that does not know it throws a TypeError rather than ignoring it. Those browsers
 * already honour EXIF by default, so the retry without the option loses nothing.
 */
const decode = async (file: File): Promise<ImageBitmap> => {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch (error) {
    if (error instanceof TypeError) return createImageBitmap(file);
    throw error;
  }
};

type PhotoToDataUrlParams = { file: File; size?: number };

/**
 * A chosen file as the data URL the profile stores: centre-cropped to a square, resized
 * to `size` and encoded as JPEG. Done here rather than trusting the file as it is, since a
 * phone photo is several MB and the column, and every user join that sends it, are not
 * the place for that. The server checks the result again and refuses anything else.
 *
 * A photo smaller than `size` is not scaled up. JPEG has no alpha, so a transparent PNG
 * or WebP is laid on white first; without that it would come out on black.
 */
export async function photoToDataUrl({ file, size = PHOTO_SIZE }: PhotoToDataUrlParams): Promise<string> {
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file');

  const bitmap = await decode(file);
  try {
    const side = Math.min(bitmap.width, bitmap.height);
    if (side === 0) throw new Error('The image is empty');
    const output = Math.min(size, side);

    const canvas = document.createElement('canvas');
    canvas.width = output;
    canvas.height = output;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not prepare the photo');

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, output, output);

    const sx = (bitmap.width - side) / 2;
    const sy = (bitmap.height - side) / 2;
    context.drawImage(bitmap, sx, sy, side, side, 0, 0, output, output);
    return canvas.toDataURL('image/jpeg', PHOTO_QUALITY);
  } finally {
    bitmap.close();
  }
}
